import { FunctionComponent, useMemo } from "react";
import {
  Icon,
  Rating,
  SelectChangeEvent,
  Typography,
  styled,
} from "@mui/material";
import {
  REVIEWS_CLEAR_FILTER_LABEL,
  REVIEWS_EMPTY_STATE_LABEL,
  REVIEWS_EMPTY_STATE_SUB_LABEL,
  REVIEWS_FILTER_LABEL,
  REVIEWS_SORT_LABEL,
} from "../../../../theme/defaultConstants";
import { Review } from "../../../dataAccess/api/review";
import Carousel from "../Carousel";
import ReviewCard from "./ReviewCard";
import DropdownList, { DropdownItem } from "../DropdownList";
import { Settings } from "../../public/Reviews";
import { TextButton } from "../TextButton";

interface Props
  extends Pick<
    Settings,
    | "clearFilterLabel"
    | "emptyStateLabel"
    | "filterLabel"
    | "sortLabel"
    | "emptyStateSubLabel"
    | "showSortOptions"
    | "showFilterOptions"
    | "reviewsShown"
    | "showRating"
    | "showReviewerName"
    | "variant"
    | "highestLabel"
    | "lowestLabel"
    | "starsLabel"
  > {
  reviews: Review[];
  setSelectedFilterOption: (option: DropdownItem) => void;
  selectedFilterOption: DropdownItem;
  setSelectedSortOption: (option: DropdownItem) => void;
  selectedSortOption: DropdownItem;
}

const FlexContainer = styled("div")(() => ({
  display: "flex",
  position: "relative",
  zIndex: 2,
}));

const EmptyStateContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
}));

const ArrowUpIcon = styled(Icon)(({ theme }) => ({
  width: "1rem",
  fontSize: "1rem !important",
  alignSelf: "center",
  fill: theme.palette.text.paper,
}));

const ArrowDownIcon = styled(Icon)(({ theme }) => ({
  width: "1rem",
  fontSize: "1rem !important",
  alignSelf: "center",
  fill: theme.palette.text.paper,
}));

export const StyledTextButton = styled(TextButton)(() => ({
  lineHeight: 0,
  padding: 0,
}));

const ReviewsList: FunctionComponent<Props> = ({
  clearFilterLabel,
  emptyStateLabel,
  filterLabel,
  sortLabel,
  emptyStateSubLabel,
  highestLabel,
  lowestLabel,
  starsLabel,
  showSortOptions,
  showFilterOptions,
  reviewsShown,
  showRating,
  showReviewerName,
  variant = "plain",
  reviews,
  selectedFilterOption,
  setSelectedFilterOption,
  selectedSortOption,
  setSelectedSortOption,
}) => {
  const sortOptions: DropdownItem[] = useMemo(
    () => [
      {
        value: "highest",
        label: highestLabel,
        icon: <ArrowUpIcon>arrow_upward</ArrowUpIcon>,
      },
      {
        value: "lowest",
        label: lowestLabel,
        icon: <ArrowDownIcon>arrow_downward</ArrowDownIcon>,
      },
    ],
    [highestLabel, lowestLabel],
  );

  const filterOptions: DropdownItem[] = useMemo(
    () =>
      Array.from({ length: 5 }, (value, index) => ({
        value: String(index + 1),
        label: starsLabel,
        icon: <Rating readOnly value={index + 1} />,
      })),
    [starsLabel],
  );

  const handleSortChange = (event: SelectChangeEvent<DropdownItem>) => {
    const currentSortOption = sortOptions.find(
      (option) => option.value === event.target.value,
    );
    if (!currentSortOption) return;

    setSelectedSortOption(currentSortOption);
  };

  const handleFilterChange = (event: SelectChangeEvent<DropdownItem>) => {
    const currentFilterOption = filterOptions.find(
      (option) => option.value === event.target.value,
    );
    if (!currentFilterOption) return;

    setSelectedFilterOption(currentFilterOption);
  };

  return (
    <>
      <FlexContainer>
        {showSortOptions && (
          <DropdownList
            selectedValue={selectedSortOption}
            onChange={handleSortChange}
            options={sortOptions}
            label={sortLabel || REVIEWS_SORT_LABEL}
            icon={<Icon>keyboard_arrow_down</Icon>}
          />
        )}
        {showFilterOptions && showRating && (
          <DropdownList
            selectedValue={selectedFilterOption}
            onChange={handleFilterChange}
            options={filterOptions}
            label={filterLabel || REVIEWS_FILTER_LABEL}
            icon={<Icon>keyboard_arrow_down</Icon>}
          />
        )}
        {(showFilterOptions || showSortOptions) && (
          <TextButton
            onClick={() => {
              setSelectedFilterOption({
                value: "",
                label: "",
              });
              setSelectedSortOption({
                value: "",
                label: "",
              });
            }}
          >
            {clearFilterLabel || REVIEWS_CLEAR_FILTER_LABEL}
          </TextButton>
        )}
      </FlexContainer>
      {reviews && reviews.length > 0 ? (
        <Carousel
          rowsPerView={reviewsShown}
          slidesPerViewOnDesktop={1}
          fullWidthSwiper
        >
          {reviews?.map((item, idx) => (
            <ReviewCard
              showRating={showRating}
              showReviewerName={showReviewerName}
              review={item}
              key={idx}
              variant={reviewsShown > 1 ? "plain" : variant}
              imageSize={reviewsShown > 1 ? 80 : 160}
            />
          ))}
        </Carousel>
      ) : (
        <EmptyStateContainer>
          <Typography variant="h5">
            {emptyStateLabel || REVIEWS_EMPTY_STATE_LABEL}
          </Typography>
          <Typography>
            {emptyStateSubLabel || REVIEWS_EMPTY_STATE_SUB_LABEL}
          </Typography>
        </EmptyStateContainer>
      )}
    </>
  );
};

export default ReviewsList;
